import {
    Box,
    Button,
    Divider,
    Grid2,
    IconButton,
    TextField,
    Tooltip,
    Typography,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE } from '../../hooks/useModal';

// Per-show rental slot template editor. Edits values.marketing.rentalSlots — an
// ordered array of { key, label, labelI18n?, timeOfDay, durationMinutes, isFullDay }.
// Each slot becomes one timeslot per rental day; the backend blocks seats between
// slots whose [start, start+duration) intervals overlap.
function SlotTemplateEditor({ values, setFieldValue, showModal }) {
    const { t } = useTranslation();
    const slots = values.marketing?.rentalSlots || [];

    const writeSlots = (next) =>
        setFieldValue('marketing', { ...values.marketing, rentalSlots: next });

    const updateSlot = (idx, patch) =>
        writeSlots(slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)));

    const removeSlot = (idx) => writeSlots(slots.filter((_, i) => i !== idx));

    const moveSlot = (idx, delta) => {
        const target = idx + delta;
        if (target < 0 || target >= slots.length) return;
        const next = [...slots];
        [next[idx], next[target]] = [next[target], next[idx]];
        writeSlots(next);
    };

    const addSlot = () =>
        writeSlots([
            ...slots,
            {
                // Stable, immutable key referenced by timeslots + ticket options.
                // Must match ^[a-z0-9_]{1,40}$, so derive it from a uuid.
                key: `slot_${crypto.randomUUID().slice(0, 8)}`,
                label: '',
                timeOfDay: '09:00',
                durationMinutes: 60,
                isFullDay: false,
            },
        ]);

    if (!values.marketing?.rentalMode) {
        return (
            <Grid2 size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                    {t('shows.columns.slotTemplateRentalOffHint')}
                </Typography>
            </Grid2>
        );
    }

    return (
        <Grid2 size={{ xs: 12 }}>
            <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1, textAlign: 'left' }}
            >
                {t('shows.columns.slotTemplateTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('shows.columns.slotTemplateHint')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {slots.map((slot, idx) => (
                <Box
                    key={slot.key ?? idx}
                    sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}
                >
                    <TextField
                        label={t('shows.columns.slotTemplateLabel')}
                        value={slot.label || ''}
                        onChange={(e) => updateSlot(idx, { label: e.target.value })}
                        required
                        size="small"
                        sx={{ flex: 2, minWidth: 160 }}
                    />
                    <Tooltip title={t('common.translations')}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={!values.venueId}
                                onClick={() =>
                                    showModal(MODAL_TYPE.TRANSLATIONS, {
                                        translations: slot.labelI18n || {},
                                        setTranslations: (translations) =>
                                            updateSlot(idx, { labelI18n: translations }),
                                        item: 'shows',
                                        column: 'rentalSlotLabel',
                                        venueId: values.venueId,
                                        originalString: slot.label,
                                    })
                                }
                            >
                                <LanguageIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <TextField
                        label={t('shows.columns.slotTemplateStartTime')}
                        type="time"
                        value={slot.timeOfDay || '09:00'}
                        onChange={(e) => updateSlot(idx, { timeOfDay: e.target.value })}
                        required
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 130 }}
                    />
                    <TextField
                        label={t('shows.columns.slotTemplateDuration')}
                        type="number"
                        value={slot.durationMinutes ?? ''}
                        onChange={(e) =>
                            updateSlot(idx, {
                                durationMinutes: e.target.value === '' ? null : Number(e.target.value),
                            })
                        }
                        required
                        size="small"
                        inputProps={{ min: 1, step: 1 }}
                        sx={{ width: 130 }}
                    />
                    <FormControlLabel
                        label={t('shows.columns.slotTemplateIsFullDay')}
                        control={
                            <Checkbox
                                checked={!!slot.isFullDay}
                                onChange={(e) => updateSlot(idx, { isFullDay: e.target.checked })}
                            />
                        }
                    />
                    <IconButton
                        size="small"
                        aria-label={t('shows.columns.slotTemplateMoveUp')}
                        onClick={() => moveSlot(idx, -1)}
                        disabled={idx === 0}
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label={t('shows.columns.slotTemplateMoveDown')}
                        onClick={() => moveSlot(idx, 1)}
                        disabled={idx === slots.length - 1}
                    >
                        <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        aria-label={t('shows.columns.slotTemplateRemove')}
                        onClick={() => removeSlot(idx)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
            <Button
                startIcon={<AddIcon />}
                size="small"
                sx={{ display: 'flex', mr: 'auto' }}
                onClick={addSlot}
            >
                {t('shows.columns.slotTemplateAdd')}
            </Button>
        </Grid2>
    );
}

export default SlotTemplateEditor;
